<template>
	<div
		:class="[
			'date-picker',
			...this.variantClassList,
			disabled && 'is-disabled',
			errorMessages.length && 'has-error'
		]"
	>
		<Label
			class="date-picker__label"
			:labelText="labelText"
			v-if="labelText"
		/>
		<div class="date-picker__content">
			<div class="date-picker__input-container">
				<input
					class="date-picker__input"
					@blur="onInputTextChanged"
					:id="uid"
					v-model="displayValue"
					:placeholder="placeholderText"
					type="text"
					autocomplete="off"
					ref="triggerBtn"
					:ariaLabel="ariaLabel"
					:style="datePickerStyle"
					@click="setExpanded(false)"
					data-test="datePickerInput"
				/>
				<i class="icon icon-error-solid" />
				<i class="icon icon-calendar" @click="setExpanded(false)" />
				<i
					class="icon icon-calendar is-mobile"
					@click="setExpandedMobile"
					data-test="expandDatePickerMobile"
				/>
			</div>
		</div>
		<div class="date-picker__menu" v-show="expanded" ref="menu">
			<div class="date-picker__calendar-mode" v-show="calendarMode">
				<DatePickerCalendar
					v-for="monthDate in displayMonths"
					:key="monthDate.toDateString()"
					:monthDate="monthDate"
					:value="currentValue"
					:selectionType="datePickerType"
					@todayClicked="onClickToday"
					@clickPreviousMonth="onClickPreviousMonth"
					@clickNextMonth="onClickNextMonth"
					@dayClicked="onDayClicked"
					@monthLabelClick="toggleMode"
					:showPreviousMonthButton="true"
					:showNextMonthButton="true"
					:startDate="startDate"
					:disableDaysBeforeStartDate="disableDaysBeforeStartDate"
					:disableDaysAfterMaxDate="disableDaysAfterMaxDate"
					:maxDate="maxDate"
				/>
				<div class="date-picker__calendar-footer">
					<div class="date-picker__today-btn">
						<Button
							className="btn--has-icon btn--link"
							labelText="Today"
							:btnClick="onClickToday"
							iconClassName="icon-calendar"
							:iconBeforeLabel="false"
							:disabled="disableTodayButton"
						/>
					</div>

					<ButtonGroup class="date-picker__footer-actions">
						<template #container>
							<Button
								className="btn--primary"
								labelText="Done"
								:btnClick="() => setExpanded(false)"
								:disabled="isDoneButtonDisabled"
							/>
							<Button
								className="btn--link"
								labelText="Clear"
								:btnClick="onClickClear"
								data-testid="clearBtn"
							/>
						</template>
					</ButtonGroup>
				</div>
			</div>
			<div class="date-picker__months-years-mode" v-show="monthYearMode">
				<DatePickerMonths v-model="tempSelectedMonth" />

				<span class="date-picker__vertical-divider">
					<Divider variant="vertical" />
				</span>

				<DatePickerYears v-model="tempSelectedYear" />

				<span class="date-picker__divider">
					<Divider />
				</span>

				<div class="date-picker__months-years-mode-footer">
					<div class="date-picker__months-years-mode-footer-actions">
						<Button
							className="btn--has-icon btn--link"
							labelText="Back to Calendar"
							iconClassName="icon-arrow-left"
							:iconBeforeLabel="true"
							:btnClick="onBackToCalendarClicked"
						/>
						<Button
							className="btn--primary"
							labelText="Done"
							:btnClick="onMonthsYearsDoneClicked"
						/>
					</div>
				</div>
			</div>
		</div>
		<PopperPosition
			trackedRefName="triggerBtn"
			popoverRefName="menu"
			placement="bottom-start"
			:popperVisible="expanded"
			strategy="fixed"
			:altBoundary="altBoundary"
		/>

		<Modals
			class="date-picker--mobile"
			v-if="expandedMobile"
			:display="true"
			:fullMobile="true"
			@show="setExpandedMobile"
			data-test="datePickerMobile"
		>
			<template #modal-content>
				<div class="date-picker--mobile__content">
					<div class="date-picker__input-container">
						<input
							class="date-picker__input"
							@blur="onInputTextChanged"
							:id="uid"
							v-model="displayValue"
							:placeholder="placeholderText"
							type="text"
							autocomplete="off"
							:ariaLabel="ariaLabel"
							:style="datePickerStyle"
						/>
						<i class="icon icon-error-solid" />
						<i
							class="icon icon-calendar is-mobile"
							@click="setExpandedMobile"
						/>
					</div>
					<div class="date-picker__menu">
						<div
							class="date-picker__calendar-mode"
							v-show="calendarMode"
						>
							<DatePickerCalendar
								v-for="monthDate in displayMonths"
								:key="monthDate.toDateString()"
								:monthDate="monthDate"
								:value="currentValue"
								:selectionType="datePickerType"
								@todayClicked="onClickToday"
								@clickPreviousMonth="onClickPreviousMonth"
								@clickNextMonth="onClickNextMonth"
								@dayClicked="onDayClicked"
								@monthLabelClick="toggleMode"
								:showPreviousMonthButton="true"
								:showNextMonthButton="true"
								:startDate="startDate"
								:disableDaysBeforeStartDate="
									disableDaysBeforeStartDate
								"
								:disableDaysAfterMaxDate="
									disableDaysAfterMaxDate
								"
								:maxDate="maxDate"
							/>
						</div>
						<div
							class="date-picker__months-years-mode"
							v-show="monthYearMode"
						>
							<DatePickerMonths v-model="tempSelectedMonth" />

							<span class="date-picker__vertical-divider">
								<Divider variant="vertical" />
							</span>

							<DatePickerYears v-model="tempSelectedYear" />

							<span class="date-picker__divider">
								<Divider />
							</span>

							<div class="date-picker__months-years-mode-footer">
								<div
									class="date-picker__months-years-mode-footer-actions"
								>
									<Button
										className="btn--has-icon btn--link"
										labelText="Back to Calendar"
										iconClassName="icon-arrow-left"
										:iconBeforeLabel="true"
										:btnClick="onBackToCalendarClicked"
									/>
									<Button
										className="btn--primary"
										labelText="Done"
										:btnClick="onMonthsYearsDoneClicked"
									/>
								</div>
							</div>
						</div>
					</div>
					<HelpFeedback
						class="date-picker__help-feedback"
						:helpText="helpText"
						v-if="helpText"
					/>
					<ErrorFeedback
						class="date-picker__error-feedback"
						:errorMessages="errorMessages"
						v-if="errorMessages.length > 0"
					/>
				</div>
			</template>
			<template #modal-footer>
				<div class="date-picker__calendar-footer">
					<div class="date-picker__today-btn">
						<Button
							className="btn--has-icon btn--link"
							labelText="Today"
							:btnClick="onClickToday"
							iconClassName="icon-calendar"
							:iconBeforeLabel="false"
						/>
					</div>

					<ButtonGroup class="date-picker__footer-actions">
						<template slot="container">
							<Button
								className="btn--primary"
								labelText="Done"
								:btnClick="setExpandedMobile"
								data-test="datePickerMobileDone"
								:disabled="isDoneButtonDisabled"
							/>
							<Button
								className="btn--link"
								labelText="Clear"
								:btnClick="onClickClear"
								data-testid="clear-btn"
							/>
						</template>
					</ButtonGroup>
				</div>
			</template>
		</Modals>
		<HelpFeedback
			class="date-picker__help-feedback"
			:helpText="helpText"
			v-if="helpText"
		/>
		<ErrorFeedback
			class="date-picker__error-feedback"
			:errorMessages="errorMessages"
			v-if="errorMessages.length > 0"
		/>
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import DatePickerCalendar from './date-picker-calendar.vue';
import DatePickerMonths from './date-picker-months.vue';
import DatePickerYears from './date-picker-years.vue';
import Label from '@/components/forms/label/label';
import ErrorFeedback from '@/components/forms/error-feedback/error-feedback';
import HelpFeedback from '@/components/forms/help-feedback/help-feedback';
import Divider from '@/components/shells/divider/divider';
import componentVariantMixin from '@/mixins/component-variant-mixin';
import variantClasses from './variant-classes/date-picker-variant-classes.js';
import ButtonGroup from '@/components/forms/button/button-group';
import PopperPosition from '@/components/visual/popper-position';
import formComponentMixin from '@/mixins/form-component-mixin';
import DocumentClickedMixin from '@/mixins/document-click';
import Modals from '@/components/shells/modal/modals';
import { DATE_RANGE, SINGLE_DATE } from './date-picker-types';
import {
	addMonths,
	compareAsc,
	differenceInCalendarMonths,
	format,
	getMonth,
	getYear,
	isValid
} from 'date-fns';
import Vue from 'vue';

export default {
	name: 'DatePicker',
	mixins: [
		componentVariantMixin,
		formComponentMixin,
		DocumentClickedMixin('onDocumentClicked')
	],
	components: {
		DatePickerCalendar,
		Label,
		ErrorFeedback,
		HelpFeedback,
		Button,
		DatePickerMonths,
		DatePickerYears,
		Divider,
		ButtonGroup,
		PopperPosition,
		Modals
	},
	data() {
		return {
			currentLowCalendarMonth: undefined,
			currentHighCalendarMonth: undefined,
			currentLowCalendarMonthNumber: undefined,
			currentHighCalendarMonthNumber: undefined,
			currentValue: this.value,
			singlePlaceHolderDateFormat: 'MM/DD/YY',
			dateFormat: 'MM/dd/yy',
			displayValue: undefined,
			calendarMode: true,
			monthYearMode: false,
			selectedMonth: undefined,
			tempSelectedMonth: undefined,
			selectedYear: undefined,
			tempSelectedYear: undefined,
			expanded: false,
			expandedMobile: false,
			dateFormatError: ''
		};
	},
	props: {
		/**
		 * dateRange or singleDate
		 */
		datePickerType: {
			type: String,
			default: DATE_RANGE
		},
		/**
		 * Single Date (date object) or null or range object in format:
		 * {
		 * 	startDate: Date,
		 * 	endDate: Date
		 * }
		 */
		value: {
			type: [Object, Date]
		},
		altBoundary: {
			type: String,
			default: undefined
		},
		labelText: {
			type: String,
			default: null
		},
		displayNumberMonths: {
			type: Number,
			default: 2
		},
		startDate: {
			type: Date,
			default: () => new Date()
		},
		helpText: {
			type: String,
			default: null
		},
		ariaLabel: {
			type: String,
			default: ''
		},
		placeholder: {
			type: String,
			default: undefined
		},
		width: {
			type: [String, Number],
			default: null
		},
		disableDaysBeforeStartDate: {
			type: Boolean,
			default: false
		},
		disableDaysAfterMaxDate: {
			type: Boolean,
			default: false
		},
		maxDate: {
			type: Date
		},
		minDate: {
			type: Date
		}
	},
	created() {
		this.variantClasses = variantClasses;
		this.setCurrentValue();
		this.setDisplayValue();
		this.updatedDatePicker();

		document.addEventListener('keyup', evt => {
			if (evt.key == 'Enter' && this.expanded) {
				this.onDocumentClicked();
				this.onInputTextChanged();
			}
		});
	},
	computed: {
		placeHolderDateFormat() {
			let result;

			if (this.datePickerType === SINGLE_DATE) {
				result = this.singlePlaceHolderDateFormat;
			} else {
				result = `${this.singlePlaceHolderDateFormat} - ${this.singlePlaceHolderDateFormat}`;
			}

			return result;
		},
		placeholderText() {
			return this.placeholder || this.placeHolderDateFormat;
		},
		displayMonths() {
			let currentDate = this.currentLowCalendarMonth;
			const months = [currentDate];

			//start at index 1, because we already added the first month
			for (let index = 1; index < this.displayNumberMonths; index++) {
				const nextDate = addMonths(currentDate, 1);

				months.push(nextDate);
				currentDate = nextDate;
			}

			return months;
		},
		datePickerStyle() {
			if (typeof this.width === 'number') {
				return {
					width: `${this.width}px`
				};
			} else {
				return {
					width: this.width
				};
			}
		},
		isDoneButtonDisabled() {
			if ('required' in this.$attrs) {
				if (this.datePickerType === SINGLE_DATE && !this.currentValue) {
					return true;
				}

				if (
					this.datePickerType === DATE_RANGE &&
					!this.currentValue.endDate
				) {
					return true;
				}
				return false;
			} else {
				if (
					this.datePickerType === DATE_RANGE &&
					this.currentValue.startDate &&
					!this.currentValue.endDate
				) {
					return true;
				}
			}
			return false;
		},
		disableTodayButton() {
			const today = new Date();
			const startDate = this.startDate ? new Date(this.startDate) : null;
			const minDate = this.minDate ? new Date(this.minDate) : null;
			const maxDate = this.maxDate ? new Date(this.maxDate) : null;

			if (this.datePickerType === 'dateRange' && (minDate || maxDate)) {
				if (minDate && today <= minDate) {
					return true;
				}

				if (maxDate) {
					maxDate.setHours(23, 59, 59, 999);
					if (today >= maxDate) {
						return true;
					}
				}
				return false;
			}

			if (this.disableDaysBeforeStartDate && startDate) {
				return startDate < today;
			}

			if (
				this.disableDaysAfterMaxDate &&
				this.currentValue.endDate &&
				maxDate
			) {
				return maxDate < today;
			}

			return false;
		}
	},
	methods: {
		setCurrentValue() {
			if (this.datePickerType === DATE_RANGE) {
				if (this.value) {
					this.currentValue = {
						startDate: this.value.startDate
							? new Date(this.value.startDate)
							: null,
						endDate: this.value.endDate
							? new Date(this.value.endDate)
							: null
					};
					this.setMonths(
						this.currentValue.startDate
							? this.currentValue.startDate
							: new Date()
					);
				} else {
					this.currentValue = {
						startDate: null,
						endDate: null
					};
					this.setMonths(new Date());
				}
			} else {
				if (this.value) {
					this.currentValue = this.value;
					this.setMonths(this.currentValue);
				} else {
					this.currentValue = null;
					this.setMonths(new Date());
				}
			}
		},
		setExpanded(preventClose) {
			if (this.expanded && this.isDoneButtonDisabled) return;

			if (preventClose && this.expanded) {
				return;
			} else if (!this.expanded && this.monthYearMode) {
				this.onBackToCalendarClicked();
			} else if (
				this.expanded &&
				this.calendarMode &&
				!this.displayValue
			) {
				this.setMonths(new Date());
			}

			this.expanded = !this.expanded;
		},
		setExpandedMobile() {
			this.expandedMobile = !this.expandedMobile;
		},
		setDisplayValue() {
			let value;

			if (this.datePickerType === SINGLE_DATE) {
				value = this.formatDate(this.currentValue);
			} else {
				value = this.formatDateRange(this.currentValue);
			}

			this.displayValue = value;
		},
		formatDateRange(dateRangeObj) {
			let formattedDateRange;
			if (
				dateRangeObj &&
				(isValid(dateRangeObj.startDate) ||
					isValid(dateRangeObj.endDate))
			) {
				if (isValid(dateRangeObj.startDate)) {
					formattedDateRange = this.formatDate(
						dateRangeObj.startDate
					);
				} else {
					formattedDateRange = this.singlePlaceHolderDateFormat;
				}

				formattedDateRange += ' - ';

				if (isValid(dateRangeObj.endDate)) {
					formattedDateRange += this.formatDate(dateRangeObj.endDate);
				} else {
					formattedDateRange += this.singlePlaceHolderDateFormat;
				}
			}

			return formattedDateRange;
		},
		formatDate(date) {
			let formattedDate;

			if (date !== undefined && isValid(date)) {
				formattedDate = format(date, this.dateFormat);
			}

			return formattedDate;
		},
		setMonths(lowCalendarMonth) {
			this.currentLowCalendarMonth = lowCalendarMonth;
			this.currentHighCalendarMonth = addMonths(
				this.currentLowCalendarMonth,
				this.displayNumberMonths - 1
			);
			this.currentLowCalendarMonthNumber = getMonth(
				this.currentLowCalendarMonth
			);
			this.currentHighCalendarMonthNumber = getMonth(
				this.currentHighCalendarMonth
			);

			this.tempSelectedMonth = getMonth(lowCalendarMonth);
			this.selectedMonth = this.tempSelectedMonth;
			this.tempSelectedYear = getYear(lowCalendarMonth);
			this.selectedYear = this.tempSelectedYear;
		},
		onClickPreviousMonth() {
			const lowMonth = addMonths(this.currentLowCalendarMonth, -1);
			this.setMonths(lowMonth);
		},
		onClickNextMonth() {
			const lowMonth = addMonths(this.currentLowCalendarMonth, 1);
			this.setMonths(lowMonth);
		},
		getMonthNumber(date) {
			return getMonth(date);
		},
		onDayClicked(day) {
			if (this.datePickerType === SINGLE_DATE) {
				this.currentValue = day;
			} else {
				if (
					!this.currentValue.startDate ||
					(compareAsc(day, this.currentValue.startDate) === -1 &&
						!this.currentValue.endDate)
				) {
					Vue.set(this.currentValue, 'startDate', day);
				} else if (!this.currentValue.endDate) {
					Vue.set(this.currentValue, 'endDate', day);
				} else {
					Vue.set(this.currentValue, 'startDate', day);
					this.currentValue.endDate = null;
				}
			}

			this.setDisplayValue();
			this.updatedDatePicker();
		},
		updatedDatePicker() {
			const { startDate, endDate } = this.currentValue;
			const isSingleDate = this.datePickerType === SINGLE_DATE;

			if (
				isSingleDate ||
				(startDate && endDate) ||
				(!startDate && !endDate)
			) {
				this.errorMessageArray = [];
				this.$emit('input', this.currentValue);
				this.$emit('change', this.currentValue);
				this.$emit('hasError', false);
			}
		},
		toggleMode() {
			this.calendarMode = !this.calendarMode;
			this.monthYearMode = !this.monthYearMode;
		},
		onClickToday() {
			var today = new Date();
			Vue.set(this.currentValue, 'today', today);

			if (this.datePickerType === DATE_RANGE) {
				Vue.set(this.currentValue, 'endDate', null);
				this.currentValue.endDate = null;
			}

			this.setDisplayValue();

			this.onDayClicked(today);
			this.setMonths(today);
			this.errorMessageArray = [];
		},
		onClickClear() {
			if (this.datePickerType === SINGLE_DATE) {
				this.currentValue = {};
			} else {
				Vue.set(this.currentValue, 'startDate', null);
				Vue.set(this.currentValue, 'endDate', null);
			}

			this.displayValue = null;
			this.updatedDatePicker();
		},
		onInputTextChanged() {
			if (
				!this.displayValue ||
				!this.displayValue.trim() ||
				this.displayValue.trim() === '-'
			) {
				this.displayValue = null;
				this.dateFormatError = null;

				if (this.dataPickerType === SINGLE_DATE) {
					this.currentValue = null;
				} else {
					Vue.set(this.currentValue, 'startDate', null);
					Vue.set(this.currentValue, 'endDate', null);
				}

				return;
			}

			if (this.datePickerType === SINGLE_DATE) {
				const displayTextDate = new Date(this.displayValue);

				if (!isValid(displayTextDate)) {
					this.dateFormatError = 'Invalid Value Entered for Date';
				} else {
					this.dateFormatError = null;
					this.currentValue = displayTextDate;
					this.setMonths(displayTextDate);
					this.setDisplayValue();
					this.updatedDatePicker();
				}
			} else {
				const displayTextDate = new Date(this.displayValue);

				if (isValid(displayTextDate)) {
					this.dateFormatError = null;
					Vue.set(this.currentValue, 'startDate', displayTextDate);
					Vue.set(this.currentValue, 'endDate', null);
					this.setMonths(displayTextDate);
					this.setDisplayValue();
					return;
				}

				const displayTextDates = this.displayValue.split('-');
				const firstDisplayTextDate = new Date(displayTextDates[0]);
				const secondDisplayTextDate = new Date(displayTextDates[1]);

				if (displayTextDates.length !== 2) {
					this.dateFormatError =
						'Invalid Value Entered for Date Range';
				} else if (
					isValid(firstDisplayTextDate) &&
					displayTextDates[1].trim() === this.dateFormat.toUpperCase()
				) {
					this.dateFormatError = null;
					Vue.set(
						this.currentValue,
						'startDate',
						firstDisplayTextDate
					);
					Vue.set(this.currentValue, 'endDate', null);
					this.setMonths(firstDisplayTextDate);
					this.setDisplayValue();
					return;
				} else if (
					!isValid(firstDisplayTextDate) ||
					!isValid(secondDisplayTextDate)
				) {
					this.dateFormatError =
						'Invalid Value Entered for Date Range';
				} else {
					this.dateFormatError = null;
					Vue.set(
						this.currentValue,
						'startDate',
						firstDisplayTextDate
					);
					Vue.set(
						this.currentValue,
						'endDate',
						secondDisplayTextDate
					);

					if (
						differenceInCalendarMonths(
							secondDisplayTextDate,
							firstDisplayTextDate
						) === 1
					) {
						this.setMonths(firstDisplayTextDate);
					} else {
						this.setMonths(secondDisplayTextDate);
					}

					this.setDisplayValue();
					this.$emit('input', this.currentValue);
					this.$emit('change', this.currentValue);
				}
			}
		},
		onDocumentClicked() {
			if (this.expanded && !this.isDoneButtonDisabled) {
				this.expanded = false;

				if (!this.displayValue) {
					this.setMonths(new Date());
				}
			}
		},
		onBackToCalendarClicked() {
			let tempDate = new Date();

			if (
				this.datePickerType === SINGLE_DATE &&
				this.currentValue.today
			) {
				tempDate = this.currentValue.today;
			} else if (
				this.datePickerType === DATE_RANGE &&
				this.currentValue.startDate
			) {
				tempDate = this.currentValue.startDate;
			}

			if (this.tempSelectedMonth) {
				tempDate.setMonth(this.tempSelectedMonth);
			}

			if (this.tempSelectedYear) {
				tempDate.setFullYear(this.tempSelectedYear);
			}

			this.setMonths(tempDate);
			this.setDisplayValue();
			this.toggleMode();
		},
		onMonthsYearsDoneClicked() {
			const today = new Date();
			const year = this.tempSelectedYear ?? getYear(today);
			const month = this.tempSelectedMonth ?? getMonth(today);

			this.setMonths(new Date(year, month));
			this.toggleMode();
		},
		closePopoverHandler() {
			this.expanded = !this.expanded;
		}
	},
	watch: {
		value() {
			this.setCurrentValue();
			this.setDisplayValue();
		},
		dateFormatError: {
			handler(val) {
				const hasError = !!val;
				if (hasError) {
					this.errorMessageArray.push(val);
				} else {
					this.errorMessageArray = [];
				}

				this.$emit('hasError', hasError);
			}
		}
	}
};
</script>

<style lang="scss" scoped>
@import '@/scss/5-objects/_form.scss';
.date-picker {
	position: relative;
	display: inline-flex;
	flex-direction: column;
	font-size: 13px;

	&__menu {
		z-index: 20;
		padding: $padding-md;
		background: $white;

		@include mq($from: sm-l, $until: md) {
			display: flex;
			justify-content: center;
		}

		@include mq($from: md) {
			border: $form-input-border;
			border-radius: $border-radius;
			@include medium-shadow;
		}
	}

	&__months-years-mode {
		display: flex;
		flex-direction: column;
		@include mq($from: sm-l) {
			display: grid;
			grid-template-columns: 1fr auto 1fr;
		}
	}

	&__months-years-mode-footer {
		@include mq($from: sm-l) {
			grid-column: 1 / 4;
		}

		&-actions {
			display: flex;
			justify-content: space-between;
		}
	}

	&__input-container {
		position: relative;
		display: flex;
		@include textfield;

		&:focus-within {
			-o-outline: $focus-outline;
			-moz-outline: $focus-outline;
			outline: $focus-outline;
			outline-offset: $focus-offset;
		}

		.icon.icon-calendar {
			display: none;

			@include mq($from: md) {
				display: inline-block;
			}
		}

		.icon.icon-calendar.is-mobile {
			display: none;

			@include mq($until: md) {
				display: inline-block;
			}
		}
	}

	&__input {
		display: inline-block;
		appearance: none;
		outline: none;
		border: none;
		@include input-height;
		cursor: pointer;
		font-size: $input-font-size;
		text-overflow: ellipsis;
		padding: $input-padding-y 42px $input-padding-y $input-padding-x;
		width: 100%;
		background-color: $white;
		border-radius: $border-radius;

		& ~ .icon {
			position: relative;
			display: inline-block;
			@include horizontal-center-with-transform;
			right: 0;
			padding: $input-padding-y $input-padding-x;
			font-size: $default-font-size;
			border-left: $form-input-border;
			cursor: pointer;
		}

		& ~ .icon-error-solid {
			display: none;
			right: 32px;
			border: none;
			color: $ui-negative;
		}
	}

	&__calendar-mode {
		&::v-deep .date-picker-calendar {
			&:nth-child(2) .date-picker-calendar__header {
				> :first-child,
				> :last-child {
					visibility: hidden;
				}
			}
		}

		@include mq($from: sm-l) {
			display: grid;
			grid-template-columns: 1fr 1fr;
			gap: $gutter-lg $gutter-xl;
			width: max-content;

			&::v-deep .date-picker-calendar {
				&:first-child .date-picker-calendar__header {
					> :last-child {
						visibility: hidden;
					}
				}

				&:nth-child(2) .date-picker-calendar__header {
					> :last-child {
						visibility: visible;
					}
				}
			}
		}
	}

	&__divider {
		grid-column: 1/4;
		display: flex;
		padding: $padding-md 0;

		&::v-deep .divider {
			width: 100%;
		}

		@include mq($until: sm-l) {
			&::v-deep .divider {
				height: 0;
			}
		}
	}

	&__vertical-divider {
		margin: $gutter-lg 0;
		&::v-deep .divider {
			height: 1px;
			width: 100%;
		}

		@include mq($from: sm-l) {
			padding: 0 $padding-md;
			margin: 0;
			&::v-deep .divider {
				height: 100%;
				width: 1px;
			}
		}
	}

	&__calendar-footer {
		grid-column: 1/3;
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-top: $gutter-md;
	}

	&.has-error {
		.date-picker {
			&__input-container {
				-o-outline: $focus-outline;
				-moz-outline: $focus-outline;
				outline: $focus-outline;
				outline-offset: $focus-offset;
				outline-color: $ui-negative;
			}

			&__input {
				padding-right: 64px;
			}
		}

		.icon-calendar {
			border-left-color: transparent;

			&::after {
				position: absolute;
				left: -1px;
				top: 0;
				display: inline-flex;
				content: '';
				height: 100%;
				width: 2px;
				background: $ui-negative;
			}
		}

		.icon-error-solid {
			display: inline-block;
		}
	}
}

.date-picker--mobile {
	.date-picker__input-container {
		@include mq($until: sm-l) {
			max-width: 80%;
			margin: auto;
		}

		@include mq($from: sm-l, $until: md) {
			margin: auto;
		}
	}

	&::v-deep {
		.modal__header {
			display: none;
		}
	}
}
</style>
